import { type PointerEvent as ReactPointerEvent, useRef } from 'react'
import { isNotDefined } from '@terros/common'

type SwipeDirection = 'left' | 'down'

type SwipeHandlers = {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void
}

const DISMISS_DISTANCE = 56

export const useSwipeToDismiss = ({
  onDismiss,
  direction,
}: {
  onDismiss: VoidFunction
  direction: SwipeDirection
}): SwipeHandlers => {
  const onDismissRef = useRef(onDismiss)
  onDismissRef.current = onDismiss
  const startRef = useRef<{ x: number; y: number } | undefined>(undefined)
  const listeningRef = useRef(false)

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>): void => {
    if (listeningRef.current) return
    if (event.pointerType === 'mouse' && event.button !== 0) return
    event.stopPropagation()
    startRef.current = { x: event.clientX, y: event.clientY }
    listeningRef.current = true

    const finish = (endEvent: PointerEvent): void => {
      listeningRef.current = false
      window.removeEventListener('pointerup', finish)
      window.removeEventListener('pointercancel', cancel)
      const start = startRef.current
      startRef.current = undefined
      if (isNotDefined(start)) return
      const deltaX = endEvent.clientX - start.x
      const deltaY = endEvent.clientY - start.y
      const dismissedLeft = direction === 'left' && deltaX <= -DISMISS_DISTANCE && Math.abs(deltaX) > Math.abs(deltaY)
      const dismissedDown = direction === 'down' && deltaY >= DISMISS_DISTANCE && Math.abs(deltaY) > Math.abs(deltaX)
      if (dismissedLeft || dismissedDown) onDismissRef.current()
    }

    const cancel = (): void => {
      listeningRef.current = false
      window.removeEventListener('pointerup', finish)
      window.removeEventListener('pointercancel', cancel)
      startRef.current = undefined
    }

    window.addEventListener('pointerup', finish)
    window.addEventListener('pointercancel', cancel)
  }

  return { onPointerDown }
}
